import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import { Hierarchy } from "../types/hierarchy";
import React, { FC, SyntheticEvent, useEffect, useState } from "react";
import DividerWithText from "./DividerWithText";
import { Accounts } from "../api/portal";
import useAuthentication from "../hooks/useAuthentication";
import {
  UserWebmap,
  UserWebmapCluster,
  UserWebmapGebiet,
} from "../context/authenticationContext";
import Loading from "./Loading";
import axios from "axios";
import HybridAutoComplete, {
  ModifiedAutoCompleteProps,
} from "./HybridAutoComplete";
import {
  gebieteGroupLabel,
  gebieteKategorie,
  sortGebiete,
} from "../util/gebiete";
import { isOnCurrentHost } from "../util/url";

const FORM_WIDTH = 250;

export type HomeWebmapsProps = {
  webmaps: UserWebmap[];
  isLoadingAdditionalAccountData: boolean;
  isAuthCompleted: boolean;
  last_webmap: number;
  last_filter: number;
  last_ars: number;
  last_nts: number;
  /**
   * Callback function for doing additional UI stuff; receives boolean value that informs
   * whether the redirect will be to a location on the same host
   */
  onOpenClick?: (willRedirectToCurrentHost: boolean) => void;
};

/**
 * React Component that displays a smart, multi-component and interdependent dropdown that
 * lets the user pick a map project as well as an area to jump into.
 *
 * It depends on a userContext provider from which it derives information about the logged in
 * user and the various loading states of the webmap information from the various map project
 * backend servers.
 *
 * Core part of the component is the hybrid autocomplete that is able to switch between client-side
 * and server-side filtering based on a provided total count of projects.
 */
const HomeWebmaps: FC<HomeWebmapsProps> = ({
  webmaps,
  isLoadingAdditionalAccountData,
  isAuthCompleted,
  last_webmap,
  last_filter,
  last_ars,
  last_nts,
  onOpenClick = () => {},
}) => {
  const [loadingAfterOpenClick, setLoadingAfterOpenClick] = useState(false);
  const [hierarchy, setHierarchy] = useState<Hierarchy>(
    last_filter === 1 ? Hierarchy.ARS : Hierarchy.NTS
  );
  const [webmap, setWebmap] = useState<UserWebmap>();
  const [gebiet, setGebiet] = useState<UserWebmapGebiet>();
  const [cluster, setCluster] = useState<UserWebmapCluster>();

  useEffect(() => {
    // if auth was successful and the webmaps are not loading, we assume access to webmaps
    if (!isLoadingAdditionalAccountData && isAuthCompleted) {
      setWebmap(() => {
        return webmaps.find((wm) => wm.id === last_webmap) as UserWebmap;
      });

      setGebiet(() => {
        return (
          webmap?.gebiete.find((g) => g.id === last_ars) || webmap?.gebiete[0]
        );
      });

      setCluster(() => {
        return (
          webmap?.cluster.find((c) => c.id === last_nts) || webmap?.cluster[0]
        );
      });
    }
  }, [
    webmaps,
    webmap?.gebiete,
    webmap?.cluster,
    isLoadingAdditionalAccountData,
    isAuthCompleted,
    last_webmap,
    last_filter,
    last_ars,
    last_nts,
  ]);

  /**
   * Callback function to pass to the autocomplete component: we get notified of a new value,
   * and set the Gebiet or Cluster, depending on the current hierarchy.
   */
  const onAutocompleteChange = (
    type: Hierarchy,
    e: SyntheticEvent,
    newValue: UserWebmapGebiet | UserWebmapCluster | null
  ) => {
    if (newValue === null || "") {
      return;
    }
    if (type === Hierarchy.ARS) {
      setGebiet(newValue as UserWebmapGebiet);
    } else {
      setCluster(newValue as UserWebmapCluster);
    }
  };

  // common props we pass to both the Gebiete and Cluster autocomplete components
  const autoCompleteProps: ModifiedAutoCompleteProps<any> = {
    disableClearable: true,
    isOptionEqualToValue: (option, value) => option.id === value.id,
    renderInput: (params) => (
      <TextField
        {...params}
        size="small"
        label="Zielgebiet"
        InputLabelProps={{ shrink: true }}
        fullWidth
      />
    ),
  };

  return (
    <Grid
      container
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
    >
      <Grid item width={FORM_WIDTH * 1.5}>
        <DividerWithText>Webmaps</DividerWithText>
      </Grid>
      {isLoadingAdditionalAccountData && (
        <Grid item>
          <Loading />
        </Grid>
      )}
      {webmap && (
        <>
          <Grid item>
            <FormControl>
              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                value={hierarchy}
                onChange={(e) => {
                  setHierarchy(e.target.value as Hierarchy);
                }}
                name="radio-buttons-group"
                row
              >
                <FormControlLabel
                  value={Hierarchy.ARS}
                  control={<Radio size="small" />}
                  label="ARS"
                  sx={{ mr: 2 }}
                />
                <FormControlLabel
                  value={Hierarchy.NTS}
                  control={<Radio size="small" />}
                  label="NTS"
                  sx={{ ml: 2, mr: 0 }}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl style={{ width: FORM_WIDTH }} sx={{ mt: 1.5 }}>
              <InputLabel id="demo-simple-select-label">Projekt</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={webmap.id}
                onChange={(e) => {
                  const webmap = webmaps.find((wm) => wm.id === e.target.value);
                  setWebmap(webmap);
                  setGebiet(webmap?.gebiete[0]);
                  setCluster(webmap?.cluster[0]);
                }}
                label="Projekt"
                size="small"
                fullWidth
              >
                {webmaps.map((webmap: UserWebmap) => {
                  return (
                    <MenuItem value={webmap.id} key={webmap.id}>
                      {webmap.bezeichner}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          {webmap && gebiet && hierarchy === Hierarchy.ARS && (
            <Grid item sx={{ mt: 1.5 }} style={{ width: FORM_WIDTH }}>
              <HybridAutoComplete
                options={
                  // assume length > 0
                  webmap.gebiete.sort(sortGebiete) as [
                    UserWebmapGebiet,
                    ...UserWebmapGebiet[]
                  ]
                }
                initialOption={gebiet}
                onValueChange={onAutocompleteChange.bind(null, Hierarchy.ARS)}
                autoCompleteProps={{
                  ...autoCompleteProps,
                  groupBy: (option) =>
                    gebieteGroupLabel[option.kategorie as gebieteKategorie],
                  getOptionLabel: (option) =>
                    `${option.bezeichner} (${option.id})`,
                }}
                totalCount={webmap.gebieteTotalCount}
                optionsGetter={(q: string) => {
                  return axios
                    .get(`${webmap.backend_url}/gebiete/ars/?q=${q}`, {
                      withCredentials: true,
                    })
                    .then((res) => res.data);
                }}
              />
            </Grid>
          )}
          {webmap && cluster && hierarchy === Hierarchy.NTS && (
            <Grid item sx={{ mt: 1.5 }} style={{ width: FORM_WIDTH }}>
              <HybridAutoComplete
                options={
                  // assume length > 0
                  webmap.cluster as [UserWebmapCluster, ...UserWebmapCluster[]]
                }
                initialOption={cluster}
                onValueChange={onAutocompleteChange.bind(null, Hierarchy.NTS)}
                autoCompleteProps={{
                  ...autoCompleteProps,
                  getOptionLabel: (option) => `NVT Cluster ${option.id}`,
                }}
                totalCount={webmap.clusterTotalCount}
                optionsGetter={(q: string) => {
                  const strippedQuery = q.replace("NVT Cluster ", "");
                  return axios
                    .get(
                      `${webmap.backend_url}/gebiete/nvt/?q=${strippedQuery}`,
                      {
                        withCredentials: true,
                      }
                    )
                    .then((res) => res.data);
                }}
              />
            </Grid>
          )}
          <Grid item>
            {/* Button seemed to be the most fitting component as there is 
                  more going on than a redirect (a simple href attribute wouldn't suffice)
              */}
            <Button
              variant="contained"
              style={{ width: FORM_WIDTH }}
              sx={{ mt: 1 }}
              size="small"
              disabled={!webmap.frontend_url}
              title={`${webmap.frontend_url}/#/map/${
                hierarchy === Hierarchy.ARS ? "ars" : "nvt"
              }/${
                (hierarchy === Hierarchy.ARS ? gebiet?.slug : cluster?.id) || ""
              }`}
              onClick={() => {
                setLoadingAfterOpenClick(true);
                const redirectTo = `${webmap.frontend_url}/#/map/${
                  hierarchy === Hierarchy.ARS ? "ars" : "nvt"
                }/${
                  (hierarchy === Hierarchy.ARS ? gebiet?.slug : cluster?.id) ||
                  ""
                }`;
                // POST new last webmap
                const zielgebietKey = hierarchy.toString();
                Accounts.patchMe({
                  last_webmap: webmap.id,
                  last_filter: hierarchy === Hierarchy.ARS ? 1 : 2,
                  [`last_${zielgebietKey}`]:
                    hierarchy === Hierarchy.ARS
                      ? (gebiet?.id as number)
                      : (cluster?.id as number),
                })
                  .then(() => {
                    // let parent components know if the host stays the same
                    onOpenClick(isOnCurrentHost(webmap.frontend_url));
                    window.location.href = redirectTo;
                  })
                  .finally(() => {
                    setLoadingAfterOpenClick(false);
                  });
              }}
            >
              {loadingAfterOpenClick ? (
                <CircularProgress sx={{ ml: 0.5 }} color="inherit" size={17} />
              ) : (
                "Öffnen"
              )}
            </Button>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export const HomeWebmapsWithContext = ({
  onOpenClick,
}: Pick<HomeWebmapsProps, "onOpenClick">) => {
  const { user, webmaps, isAuthCompleted, isLoadingAdditionalAccountData } =
    useAuthentication();

  if (!user) {
    return <Loading />;
  }

  if (webmaps === undefined) {
    return <Loading />;
  }

  return (
    <HomeWebmaps
      webmaps={webmaps}
      last_ars={user.last_ars}
      last_filter={user.last_filter}
      last_nts={user.last_nts}
      last_webmap={user.last_webmap}
      isLoadingAdditionalAccountData={isLoadingAdditionalAccountData}
      isAuthCompleted={isAuthCompleted}
      onOpenClick={onOpenClick}
    />
  );
};

export default HomeWebmaps;
